import * as XLSX from 'xlsx';

interface InvoiceExport {
  cliente: string;
  cpf_cnpj: string;
  valor: string;
  vencimento: string;
  pago_em: string;
  status: string;
  descricao: string;
}

interface Invoice {
  id: string;
  amount: number;
  due_date: string;
  status: string;
  paid_at: string | null;
  description: string | null;
  customer: {
    name: string;
    tax_id: string;
  };
}

interface CustomerExport {
  nome: string;
  cpf_cnpj: string;
  email: string;
  whatsapp: string;
  telefone: string;
  endereco: string;
  cidade: string;
  estado: string;
  situacao: string;
  regua: string;
  criado_em: string;
}

export interface Customer {
  id: string;
  name: string;
  tax_id: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  whatsapp: string | null;
  cluster: 'bom_pagador' | 'regular' | 'mau_pagador' | null;
  created_at?: string;
  collection_rule_id?: string | null;
}

export interface CollectionRule {
  id: string;
  name: string;
}

const statusLabels: Record<string, string> = {
  pending: 'Pendente',
  paid: 'Pago',
  overdue: 'Vencido',
  cancelled: 'Cancelado'
};

const clusterLabels: Record<string, string> = {
  bom_pagador: 'Bom Pagador',
  regular: 'Regular',
  mau_pagador: 'Inadimplente'
};

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value / 100);
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('pt-BR');
}

export function exportInvoicesToExcel(invoices: Invoice[], fileName: string = 'faturas') {
  const data: InvoiceExport[] = invoices.map(inv => ({
    cliente: inv.customer.name,
    cpf_cnpj: inv.customer.tax_id,
    valor: formatCurrency(inv.amount),
    vencimento: formatDate(inv.due_date),
    pago_em: formatDate(inv.paid_at),
    status: statusLabels[inv.status] || inv.status,
    descricao: inv.description || '-'
  }));

  const worksheet = XLSX.utils.json_to_sheet(data);
  
  // Set column widths
  worksheet['!cols'] = [
    { wch: 30 }, // cliente
    { wch: 18 }, // cpf_cnpj
    { wch: 15 }, // valor
    { wch: 12 }, // vencimento
    { wch: 12 }, // pago_em
    { wch: 12 }, // status
    { wch: 40 }, // descricao
  ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Faturas');

  // Generate file name with date
  const today = new Date().toISOString().split('T')[0];
  XLSX.writeFile(workbook, `${fileName}_${today}.xlsx`);
}

export function exportCustomersToExcel(
  customers: Customer[], 
  collectionRules: CollectionRule[] = [],
  fileName: string = 'clientes'
) {
  const ruleMap = collectionRules.reduce((acc, rule) => {
    acc[rule.id] = rule.name;
    return acc;
  }, {} as Record<string, string>);

  const data: CustomerExport[] = customers.map(c => ({
    nome: c.name,
    cpf_cnpj: c.tax_id,
    email: c.email || 'PENDENTE',
    whatsapp: c.whatsapp || 'PENDENTE',
    telefone: c.phone || 'PENDENTE',
    endereco: c.address || 'PENDENTE',
    cidade: c.city || '-',
    estado: c.state || '-',
    situacao: c.cluster ? clusterLabels[c.cluster] || 'Regular' : 'Regular',
    regua: c.collection_rule_id ? (ruleMap[c.collection_rule_id] || 'Automática') : 'Sem régua',
    criado_em: formatDate(c.created_at || null)
  }));

  const worksheet = XLSX.utils.json_to_sheet(data);
  
  worksheet['!cols'] = [
    { wch: 30 }, // nome
    { wch: 18 }, // cpf_cnpj
    { wch: 25 }, // email
    { wch: 15 }, // whatsapp
    { wch: 15 }, // telefone
    { wch: 35 }, // endereco
    { wch: 15 }, // cidade
    { wch: 5 },  // estado
    { wch: 12 }, // situacao
    { wch: 20 }, // regua
    { wch: 12 }, // criado_em
  ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Clientes');

  const today = new Date().toISOString().split('T')[0];
  XLSX.writeFile(workbook, `${fileName}_${today}.xlsx`);
}
